/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

(() => {
  if (customElements.get('shopping-bag')) return;

  class ShoppingBag extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.addEventListener('click', event => this.onClick(event), { signal });
      this.addEventListener('change', event => this.onChange(event), { signal });
      this.addEventListener('submit', event => this.onSubmit(event), { signal });
      document.addEventListener('theme:cart-updated', event => {
        if (event.detail?.source === this) return;
        if (this.pending) this.refreshAfterUpdate = true;
        else this.refresh(event.detail?.sections).catch(error => this.refreshError(error));
      }, { signal });
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.refresh().catch(error => this.refreshError(error));
      }, { signal });
    }

    disconnectedCallback() { this.controller?.abort(); }

    onClick(event) {
      const control = event.target.closest('[data-bag-quantity], [data-bag-remove-code]');
      if (!control || !this.contains(control)) return;
      event.preventDefault();
      if (this.pending) return;
      if (control.hasAttribute('data-bag-remove-code')) {
        const codes = this.codes().filter(code => code !== control.dataset.bagRemoveCode);
        this.update('update', { discount: codes.join(',') });
      } else {
        const row = control.closest('[data-bag-key]');
        this.update('change', { id: row.dataset.bagKey, quantity: Number(control.dataset.bagQuantity) });
      }
    }

    onChange(event) {
      const input = event.target.closest('[data-bag-quantity-input]');
      if (!input || this.pending) return;
      const quantity = Number(input.value);
      // Zero removes the line; positive quantities follow Shopify's quantity rules.
      if (!input.checkValidity() || !Number.isInteger(quantity) || (quantity > 0 && quantity < Number(input.dataset.minimum))) {
        input.reportValidity();
        input.value = input.dataset.originalQuantity;
        return;
      }
      if (quantity === Number(input.dataset.originalQuantity)) return;
      this.update('change', { id: input.closest('[data-bag-key]').dataset.bagKey, quantity });
    }

    onSubmit(event) {
      if (event.target.matches('[data-bag-discount-form]')) {
        event.preventDefault();
        if (this.pending) return;
        const code = event.target.elements.discount.value.trim();
        if (!code) return;
        this.update('update', { discount: [...new Set([...this.codes(), code])].join(',') }, code);
      } else if (event.target.matches('[data-bag-form]')) {
        // Leave Shopify's native checkout submission intact.
        if (this.pending || event.submitter?.name !== 'checkout') event.preventDefault();
      }
    }

    codes() { return [...this.querySelectorAll('[data-bag-code]')].map(node => node.dataset.bagCode); }

    sectionIds() {
      return [this.dataset.sectionId, document.querySelector('[data-header-cart]')?.dataset.sectionId].filter(Boolean);
    }

    focusTarget() {
      const active = document.activeElement;
      if (!this.contains(active)) return null;
      const row = active.closest('[data-bag-key]');
      return { key: row?.dataset.bagKey, line: row?.dataset.bagLine, control: active.dataset.bagFocus };
    }

    restoreFocus(target) {
      if (!target) return;
      let scope = this;
      if (target.key) {
        const rows = [...this.querySelectorAll('[data-bag-key]')];
        scope = rows.find(row => row.dataset.bagKey === target.key)
          || rows.find(row => row.dataset.bagLine === target.line) || rows.at(-1) || this;
      }
      const control = [...scope.querySelectorAll('[data-bag-focus]')].find(node => node.dataset.bagFocus === target.control && !node.disabled);
      (control || this.querySelector('[data-bag-heading]'))?.focus({ preventScroll: true });
    }

    async refresh(sections, focus = this.focusTarget()) {
      // Only the newest refresh may replace the page after concurrent header updates.
      const version = this.renderVersion = (this.renderVersion || 0) + 1;
      let html = sections?.[this.dataset.sectionId];
      if (!html) {
        const url = `${this.dataset.cartUrl}?sections=${encodeURIComponent(this.dataset.sectionId)}`;
        // /cart negotiates cart JSON for application/json, ignoring section rendering.
        const response = await fetch(url);
        if (!response.ok) throw new Error(this.dataset.errorMessage);
        html = (await response.json())[this.dataset.sectionId];
      }
      if (!html) throw new Error(this.dataset.errorMessage);
      const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bag-content]');
      if (!content) throw new Error(this.dataset.errorMessage);
      if (version !== this.renderVersion || !this.isConnected) return;
      this.querySelector('[data-bag-content]').replaceWith(content);
      this.restoreFocus(focus);
    }

    showError(message = '') {
      const error = this.querySelector('[data-bag-error]');
      error.textContent = message;
      error.hidden = !message;
    }

    refreshError(error) {
      console.error('Shopping bag refresh failed:', error);
      this.showError(this.dataset.errorMessage);
    }

    async update(endpoint, payload, submittedCode) {
      if (this.pending) return;
      this.pending = true;
      this.renderVersion = (this.renderVersion || 0) + 1;
      this.showError();
      this.querySelector('[data-bag-status]').textContent = '';
      this.setAttribute('aria-busy', 'true');
      const focus = this.focusTarget();
      const controls = [...this.querySelectorAll('button, input')].filter(control => !control.disabled);
      controls.forEach(control => { control.disabled = true; });
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/${endpoint}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, sections: this.sectionIds(), sections_url: this.dataset.cartUrl })
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || cart.message || this.dataset.errorMessage);
        // Publish the server result even if a section failed to render. Never retry a mutation.
        document.dispatchEvent(new CustomEvent('theme:cart-updated', { detail: { cart, sections: cart.sections, source: this } }));
        await this.refresh(cart.sections, focus);
        this.querySelector('[data-bag-status]').textContent = this.dataset.updatedMessage;
        if (submittedCode && !this.codes().some(code => code.toLowerCase() === submittedCode.toLowerCase())) {
          this.showError(this.dataset.discountError);
          const input = this.querySelector('[name="discount"]');
          if (input) input.value = submittedCode;
        }
      } catch (error) {
        this.querySelectorAll('[data-bag-quantity-input]').forEach(input => { input.value = input.dataset.originalQuantity; });
        this.showError(error.message || this.dataset.errorMessage);
      } finally {
        controls.forEach(control => { control.disabled = false; });
        this.removeAttribute('aria-busy');
        this.pending = false;
        if (this.refreshAfterUpdate) {
          this.refreshAfterUpdate = false;
          this.refresh().catch(error => this.refreshError(error));
        }
      }
    }
  }

  customElements.define('shopping-bag', ShoppingBag);
})();
